import React, { useContext, useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import DeviceOrientationContext from "../../context/DeviceOrientationContext";
import { Text, View } from "../../shared/Themed";
import {
  DataTable,
  ThemeProvider,
  useTheme as usePaperTheme,
} from "react-native-paper";
import * as Services from "../../../utils/common";
import { Badge } from "@react-native-material/core";
import { Alert, StyleSheet } from "react-native";
import { useTranslation } from "../../../utils/useTranslation";

import Icon from "react-native-vector-icons/FontAwesome5";
import FeatherIcon from "react-native-vector-icons/Feather";
import { ScrollView, TouchableOpacity } from "react-native-gesture-handler";
import Spinner from "../../shared/Spinner";

import { default as statusColorCodes } from "../../../utils/constants/statusColorCodes";
import { ReadOnlyProps } from "../../../utils/templates";
import { NestedDataTable } from "./NestedDataTableWithPagination";
import { formatValueAsDataType } from "../../../utils/common";
import { NoDataBanner } from "../../shared/NoDataBanner";
import { MaterialCommunityIcons, SimpleLineIcons } from "@expo/vector-icons";
import CustomProgressBar from "../../../screens/ItemTracker/utils/CustomProgressBar";
import {
  Menu,
  MenuOptions,
  MenuOption,
  MenuTrigger,
} from "react-native-popup-menu";
import DeliveryProgressBar from "../../../screens/ItemTracker/utils/CustomDeliveryBar";
import OrderStatusHelp from "./tracker/OrderStatusModal";
import { useAppTheme } from "../../../hooks/useAppTheme";

interface Props {
  headers: any;
  rows: Array<any>;
  itemsPerPage: number;
  useModal: boolean;
  isDataLoading?: boolean;
  isDisplayTimeWithDate?: boolean;
  setModalVisible: (event: any) => void;
  setDeliveryModalVisible?: any;
  setDeliveryStatusModal?: any;
  handleRowClick: (event: any) => void;
  setSelected: (event: any) => void;
  setSelectedDeliveryItem?: (event: any) => void;

  //Nested Table Variables
  rowKeyValue?: string;
  nestedItemKeyValue?: string;
  nestedHeader?: any;
  isRowsExpandable?: boolean;
  isNestedDataLoaded?: boolean;
  isToExpandAllRows?: boolean;

  isDetail?: boolean; //Control Modal variable
  isEdit?: boolean; //Edit Button variable
  isDelete?: boolean; //Delete Button variable
  isRegister?: boolean; //Register Button variable(only for container registration to one stop)
  isSubDetail?: boolean; //Control Sub Modal variable
  handleDeleteRow: (id: string | number) => void;

  //exclude column to not have thousand separator format
  columnsToNotHaveThousandSeparator?: Array<string>;

  //handle sorting
  handleSortColumn?: (columnKey: string) => void;
  handleNestedSortColumn?: (
    columnKey: string,
    columnParentIdentifier: string,
  ) => void;

  operationsScreen?: boolean;
  disableHeader?: boolean;
  operationActions?: (order: any, action: string) => void;
  navigatePressAction?: (filterField: string, filterValue: string) => void;
  isDeliveryAttachment?: boolean;
  showAttachmentIcon?: boolean;
  showDeliveryStatusIcon?: boolean;
  hideInfoIcon?: boolean;
  itemTracker?: boolean;
  selectable?: boolean;
  expandTriggerColumnKey?: string;
  showExpandTrigger?: (row: any) => boolean;
  disableNestedRowActions?: boolean;
  handleDownloadPdf?: (row: any) => void;
  attachmentCheck?: Record<string, boolean>;
  hidePagination?: boolean;
  page?: number;
  setPage?: any;
  handleRegisterRow?: (id: string | number) => void;
  isContainerRegistering?: boolean;
  registeringContainerNumber?: string | null;
  topCornerRadius?: number;
  bottomCornerRadius?: number;
  // Display-only label for status badges; colour still keys off the raw value
  formatStatusLabel?: (status: string) => string;
}

export const DataTableWithPagination: React.FC<ReadOnlyProps<Props>> = ({
  headers,
  rows,
  itemsPerPage,
  useModal,
  nestedHeader,
  rowKeyValue,
  nestedItemKeyValue,
  isDisplayTimeWithDate = false,
  isDataLoading = false,
  isRowsExpandable = false,
  isNestedDataLoaded = false,
  isToExpandAllRows = false,
  setModalVisible,
  setDeliveryModalVisible,
  setDeliveryStatusModal,
  handleRowClick,
  setSelected,
  isDetail = false,
  isSubDetail = false,
  isEdit = false,
  isDelete = false,
  isRegister = false,
  handleDeleteRow,
  handleRegisterRow,
  isContainerRegistering = false,
  registeringContainerNumber = null,
  columnsToNotHaveThousandSeparator,
  handleSortColumn,
  handleNestedSortColumn,
  operationsScreen,
  disableHeader,
  operationActions,
  isDeliveryAttachment = false,
  showAttachmentIcon = true,
  showDeliveryStatusIcon = true,
  hideInfoIcon = false,
  setSelectedDeliveryItem,
  itemTracker = false,
  navigatePressAction,
  selectable = false,
  expandTriggerColumnKey,
  showExpandTrigger,
  disableNestedRowActions = false,
  handleDownloadPdf,
  attachmentCheck,
  hidePagination = false,
  page: externalPage,
  setPage: externalSetPage,
  topCornerRadius,
  bottomCornerRadius,
  formatStatusLabel,
}: ReadOnlyProps<Props>) => {
  const { colors: themeColors, isDark } = useAppTheme();
  const paperTheme = usePaperTheme();
  const styles = createStyles(themeColors);
  const { isSmallDevice, isMobileDevice } = useContext(
    DeviceOrientationContext,
  );
  const { t } = useTranslation();
  const { recentUploads } = useSelector((state: any) => state.delivery || {});
  const [currentRow, setCurrentRow] = useState<string>("");
  const [expandRow, setExpandRow] = useState<boolean>(false);
  const [expandAllRows, setExpandAllRows] = useState<boolean>(false);
  const [_itemsPerPage] = useState<number>(itemsPerPage);

  const [internalPage, setInternalPage] = useState<number>(0);
  const page = externalPage !== undefined ? externalPage : internalPage;
  const setPage =
    externalSetPage !== undefined ? externalSetPage : setInternalPage;

  const lastPressRef = useRef<{ time: number; value: string; rowValue: any }>({
    time: 0,
    value: "",
    rowValue: null,
  });
  const handleDoublePress = (value: string, rowValue: any) => {
    const time = new Date().getTime();
    const delta = time - lastPressRef.current.time;

    if (
      delta < 300 &&
      lastPressRef.current.value === value &&
      lastPressRef.current.rowValue === rowValue
    ) {
      navigatePressAction?.(value, rowValue);
    }
    lastPressRef.current = { time, value, rowValue };
  };

  const fromPage = (
    page: number,
    itemPerPage: number,
    rows: any[] | undefined,
  ) => {
    return page * itemPerPage + itemPerPage > (rows || []).length
      ? (rows || []).length
      : page * itemPerPage + itemPerPage;
  };

  useEffect(() => {
    setExpandAllRows(isToExpandAllRows);
  }, [isToExpandAllRows]);

  useEffect(() => {
    const numberOfPages = Math.ceil((rows?.length || 0) / _itemsPerPage);
    if (page > 0 && page >= numberOfPages) {
      setPage(0);
    }
  }, [rows.length]);

  const [statusHelpVisible, setStatusHelpVisible] = useState(false);

  const handleOrderStatusHelp = () => {
    setStatusHelpVisible(true);
  };

  const handleRowClicking = (row: any, clickType: string) => {
    if (useModal && clickType === "details") {
      setModalVisible(true);
      setSelected(row);
    } else if (clickType === "edit") {
      handleRowClick(row);
    } else if (clickType === "delivery") {
      setDeliveryModalVisible(true);
      setSelectedDeliveryItem(row);
    } else if (clickType === "delivery_status") {
      setDeliveryStatusModal(true);
      setSelectedDeliveryItem(row);
    } else if (!useModal && clickType === "details" && operationActions) {
      operationActions(row, clickType);
    }
  };

  const headersView = (headers: any) => {
    const views: Array<any> = [];
    if (headers && !itemTracker) {
      Object.keys(headers).forEach((value, index) => {
        views.push(
          <DataTable.Title
            style={{
              flex: headers[value].width,
              height: "auto",
              paddingVertical: 8,
              paddingHorizontal: 6,
            }}
            numberOfLines={3}
            key={Services.makeid(10)}
            sortDirection={
              headers[value].isSort && headers[value].sortDirection === "asc"
                ? "ascending"
                : headers[value].isSort &&
                    headers[value].sortDirection === "desc"
                  ? "descending"
                  : undefined
            }
          >
            <View style={{ width: "100%" }}>
              <Text
                numberOfLines={3}
                style={{
                  fontSize: 10,
                  fontWeight: "bold",
                  width: "100%",
                  textAlign: headers[value].textAlign,
                }}
              >
                {index === 0 &&
                  isRowsExpandable &&
                  !itemTracker &&
                  !expandTriggerColumnKey && (
                    <>
                      <TouchableOpacity
                        onPress={() => handleExpandableAllRowIcon()}
                      >
                        <Icon
                          name={!expandAllRows ? "plus-square" : "minus-square"}
                          size={15}
                          color={
                            headers[value].color
                              ? headers[value].color
                              : themeColors.text
                          }
                        />
                      </TouchableOpacity>
                      &nbsp;&nbsp;
                    </>
                  )}
                <TouchableOpacity onPress={() => handleSortColumn?.(value)}>
                  <Text
                    style={{
                      color: headers[value].color
                        ? headers[value].color
                        : themeColors.text,
                      fontWeight: headers[value].fontWeight
                        ? headers[value].fontWeight
                        : 700,
                    }}
                  >
                    {t(headers[value].name)}
                  </Text>
                </TouchableOpacity>{" "}
              </Text>
            </View>
          </DataTable.Title>,
        );
      });
      return views;
    }

    if (headers && itemTracker) {
      Object.keys(headers).forEach((value, index) => {
        views.push(
          <DataTable.Title
            style={{
              flex: headers[value].width,
              height: "auto",
              paddingVertical: 8,
              paddingHorizontal: 6,
            }}
            numberOfLines={3}
            key={value}
            sortDirection={
              headers[value].isSort && headers[value].sortDirection === "asc"
                ? "ascending"
                : headers[value].isSort &&
                    headers[value].sortDirection === "desc"
                  ? "descending"
                  : undefined
            }
          >
            <View style={{ width: "100%" }}>
              <Text
                numberOfLines={3}
                style={{
                  fontSize: 10,
                  fontWeight: "bold",
                  width: "100%",
                  textAlign: headers[value].textAlign,
                }}
              >
                {index === 0 &&
                  isRowsExpandable &&
                  !itemTracker &&
                  !expandTriggerColumnKey && (
                    <>
                      <TouchableOpacity
                        onPress={() => handleExpandableAllRowIcon()}
                      >
                        <Icon
                          name={!expandAllRows ? "plus-square" : "minus-square"}
                          size={15}
                          color={
                            headers[value].color
                              ? headers[value].color
                              : themeColors.text
                          }
                        />
                      </TouchableOpacity>
                      &nbsp;&nbsp;
                    </>
                  )}
                <TouchableOpacity onPress={() => handleSortColumn?.(value)}>
                  <Text
                    style={{
                      color: headers[value].color
                        ? headers[value].color
                        : themeColors.text,
                      fontWeight: 700,
                      fontSize: 10,
                      // paddingLeft: index === 0 ? 30 : 0,
                    }}
                  >
                    {t(headers[value].name)}
                  </Text>
                </TouchableOpacity>
                {/* OrderStatus help icon */}
                {headers[value].name === "orderStatus" && (
                  <TouchableOpacity
                    onPress={handleOrderStatusHelp}
                    style={{ marginLeft: 2 }}
                  >
                    <Icon name="question-circle" size={12} color="gray" />
                  </TouchableOpacity>
                )}
              </Text>
            </View>
          </DataTable.Title>,
        );
      });
      return views;
    }
  };

  const displayExpandableIcons = (
    row: any,
    expandRow: boolean,
    expandAllRows: boolean,
  ): string => {
    if (!expandRow && !expandAllRows) {
      return "plus-square";
    } else if (row[rowKeyValue as string] === currentRow || expandAllRows) {
      return "minus-square";
    } else {
      return "plus-square";
    }
  };

  // Function to get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case "On Track":
      case "Shipped":
        return "#00C853"; // Green
      case "At Risk":
        return "#FFAB00"; // Orange
      case "Pending":
        return "#D32F2F"; // Red
      case "Completed":
      case "Closed":
        return "#757575"; // Gray
      default:
        return themeColors.text; // Default Black
    }
  };

  const getDaysLeftColor = (days: any) => {
    const dayNum = typeof days === "number" ? days : parseInt(days) || 0;

    if (dayNum < 0) {
      return themeColors.text;
    } else if (dayNum < 14) {
      return "red";
    } else if (dayNum > 60) {
      return "green";
    } else if (dayNum >= 14 && dayNum <= 60) {
      return "#FFB12B";
    } else {
      return themeColors.text; // fallback
    }
  };

  const handleExpandableAllRowIcon = () => {
    setCurrentRow("");
    setExpandAllRows(!expandAllRows);
  };

  const handleExpandableRowIcon = (row: any, currentIcon: string) => {
    const isRowExpanded = currentIcon === "plus-square";
    setCurrentRow(row[rowKeyValue as string]);
    setExpandRow(isRowExpanded);
  };

  const cellsView = (rows: Array<any>, headers: any) => {
    const rowsViews: Array<React.ReactNode> = [];
    rows.forEach((row, index) => {
      let views: Array<React.ReactNode> = [];
      const switchToExpand =
        (expandRow || expandAllRows) &&
        (row[rowKeyValue as string] === currentRow || expandAllRows);

      Object.keys(headers).map((value, index) => {
        if (row) {
          if (!["status", "ship_status"].includes(value) && !itemTracker) {
            views.push(
              <View
                style={{
                  flex: headers[value].width,
                  borderColor: themeColors.borderSubtle,
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "transparent",
                  paddingHorizontal: 6,
                }}
                key={value}
              >
                {index === 0 && isRowsExpandable && !expandTriggerColumnKey ? (
                  <View
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignSelf: "flex-start",
                      alignItems: "flex-start",
                      backgroundColor: "transparent",
                    }}
                  >
                    {!expandAllRows && (
                      <TouchableOpacity
                        style={{ marginTop: 3 }}
                        onPress={() =>
                          handleExpandableRowIcon(
                            row,
                            displayExpandableIcons(
                              row,
                              expandRow,
                              expandAllRows,
                            ),
                          )
                        }
                      >
                        <Icon
                          name={displayExpandableIcons(
                            row,
                            expandRow,
                            expandAllRows,
                          )}
                          size={15}
                          color={
                            operationsScreen
                              ? themeColors.text
                              : "rgb(47, 149, 220)"
                          }
                        />
                      </TouchableOpacity>
                    )}
                    <Text
                      selectable={true}
                      style={[
                        operationsScreen && { fontWeight: "500" },
                        { userSelect: "text", fontSize: 12 } as any,
                      ]}
                    >
                      &nbsp;{row[value]}
                    </Text>
                  </View>
                ) : value === "" ? (
                  <View
                    style={{
                      flexDirection: "row",
                      alignItems: "center",
                      backgroundColor: "transparent",
                    }}
                  >
                    {(() => {
                      const ActionButton = ({
                        onPress,
                        disabled,
                        style,
                        hitSlop,
                        children,
                        isPopup,
                      }: any) => {
                        if (isPopup) {
                          return (
                            <MenuOption
                              onSelect={onPress}
                              disabled={disabled}
                              customStyles={{ optionWrapper: style }}
                            >
                              {children}
                            </MenuOption>
                          );
                        }
                        return (
                          <TouchableOpacity
                            onPress={onPress}
                            disabled={disabled}
                            style={[style, { backgroundColor: "transparent" }]}
                            hitSlop={hitSlop}
                          >
                            {children}
                          </TouchableOpacity>
                        );
                      };

                      const renderActionButtons = (isPopup: boolean) => {
                        const pad = isPopup ? 8 : 12;
                        const minSize = isPopup ? 36 : 44;
                        const iconSize = isPopup ? 16 : 18;
                        const infoIconSize = isPopup ? 18 : 22;

                        return (
                          <>
                            {isDeliveryAttachment && !hideInfoIcon && (
                              <ActionButton
                                isPopup={isPopup}
                                onPress={() => {
                                  const docNum =
                                    row.DocNum || row.documentNumber;
                                  if (
                                    attachmentCheck &&
                                    attachmentCheck[docNum] === false
                                  ) {
                                    return;
                                  }
                                  handleDownloadPdf?.(row);
                                }}
                                disabled={
                                  attachmentCheck
                                    ? attachmentCheck[
                                        row.DocNum || row.documentNumber
                                      ] === false
                                    : false
                                }
                                style={{
                                  cursor:
                                    attachmentCheck &&
                                    attachmentCheck[
                                      row.DocNum || row.documentNumber
                                    ] === false
                                      ? "default"
                                      : "pointer",
                                  padding: pad,
                                  marginRight: 2,
                                  minWidth: minSize,
                                  minHeight: minSize,
                                  justifyContent: "center",
                                  alignItems: "center",
                                  opacity:
                                    attachmentCheck &&
                                    attachmentCheck[
                                      row.DocNum || row.documentNumber
                                    ] === false
                                      ? 0.35
                                      : 1,
                                }}
                              >
                                <Icon
                                  name="file-pdf"
                                  size={iconSize}
                                  color={
                                    attachmentCheck &&
                                    attachmentCheck[
                                      row.DocNum || row.documentNumber
                                    ] === false
                                      ? "#94A3B8"
                                      : operationsScreen
                                        ? "#000"
                                        : "rgb(47, 149, 220)"
                                  }
                                />
                              </ActionButton>
                            )}
                            {isDeliveryAttachment && showDeliveryStatusIcon && (
                              <ActionButton
                                isPopup={isPopup}
                                style={{
                                  cursor: "pointer",
                                  padding: pad,
                                  marginRight: 2,
                                  minWidth: minSize,
                                  minHeight: minSize,
                                  justifyContent: "center",
                                  alignItems: "center",
                                }}
                                disabled={
                                  row?.CustomerPortalOrderNo ? false : true
                                }
                                onPress={() =>
                                  handleRowClicking(row, "delivery_status")
                                }
                              >
                                <Icon
                                  name={"truck"}
                                  size={iconSize}
                                  disabled
                                  color={
                                    !row?.CustomerPortalOrderNo
                                      ? "#DDDDDD"
                                      : "rgb(47, 149, 220)"
                                  }
                                />
                              </ActionButton>
                            )}
                            {isDetail && !hideInfoIcon && (
                              <ActionButton
                                isPopup={isPopup}
                                onPress={() =>
                                  handleRowClicking(row, "details")
                                }
                                hitSlop={{
                                  top: 20,
                                  bottom: 20,
                                  left: 20,
                                  right: 20,
                                }}
                                style={{
                                  padding: pad,
                                  marginRight: 2,
                                  minWidth: minSize,
                                  minHeight: minSize,
                                  justifyContent: "center",
                                  alignItems: "center",
                                }}
                              >
                                <FeatherIcon
                                  name={
                                    isDeliveryAttachment ? "paperclip" : "info"
                                  }
                                  size={
                                    isDeliveryAttachment
                                      ? iconSize
                                      : infoIconSize
                                  }
                                  color={
                                    isDeliveryAttachment &&
                                    (recentUploads || []).some(
                                      (u: any) =>
                                        String(u.docNum) === String(row.DocNum),
                                    )
                                      ? "#10B981" // green
                                      : operationsScreen
                                        ? "#000"
                                        : "rgb(47, 149, 220)"
                                  }
                                />
                              </ActionButton>
                            )}
                            {isRegister && (
                              <ActionButton
                                isPopup={isPopup}
                                onPress={() =>
                                  handleRegisterRow?.(
                                    row[rowKeyValue as string] as string,
                                  )
                                }
                                disabled={
                                  isContainerRegistering &&
                                  registeringContainerNumber ===
                                    row[rowKeyValue as string]
                                }
                                style={{
                                  cursor: "pointer",
                                  padding: pad,
                                  marginRight: 2,
                                  minWidth: minSize,
                                  minHeight: minSize,
                                  justifyContent: "center",
                                  alignItems: "center",
                                }}
                              >
                                {isContainerRegistering &&
                                registeringContainerNumber ===
                                  row[rowKeyValue as string] ? (
                                  <Spinner
                                    size="small"
                                    color="rgb(47, 149, 220)"
                                  />
                                ) : (
                                  <FeatherIcon
                                    name={
                                      row.oneStopTracking?.isRegistered
                                        ? "wifi"
                                        : "wifi-off"
                                    }
                                    size={iconSize}
                                    color={
                                      row.oneStopTracking?.isRegistered
                                        ? "#10B981"
                                        : operationsScreen
                                          ? "#000"
                                          : "rgb(47, 149, 220)"
                                    }
                                  />
                                )}
                              </ActionButton>
                            )}
                            {isDeliveryAttachment && showAttachmentIcon && (
                              <ActionButton
                                isPopup={isPopup}
                                onPress={() =>
                                  handleRowClicking(row, "delivery")
                                }
                                style={{
                                  cursor: "pointer",
                                  padding: pad,
                                  marginRight: 2,
                                  minWidth: minSize,
                                  minHeight: minSize,
                                  justifyContent: "center",
                                  alignItems: "center",
                                }}
                              >
                                <FeatherIcon
                                  name={"paperclip"}
                                  size={iconSize}
                                  color={
                                    isDeliveryAttachment &&
                                    (recentUploads || []).some(
                                      (u: any) =>
                                        String(u.docNum) === String(row.DocNum),
                                    )
                                      ? "#10B981" // green
                                      : operationsScreen
                                        ? "#000"
                                        : "rgb(47, 149, 220)"
                                  }
                                />
                              </ActionButton>
                            )}
                            {isEdit && (
                              <ActionButton
                                isPopup={isPopup}
                                onPress={() => handleRowClicking(row, "edit")}
                                style={{
                                  cursor: "pointer",
                                  padding: pad,
                                  marginRight: 2,
                                  minWidth: minSize,
                                  minHeight: minSize,
                                  justifyContent: "center",
                                  alignItems: "center",
                                }}
                              >
                                <FeatherIcon
                                  name={"edit-2"}
                                  size={iconSize}
                                  color={
                                    operationsScreen
                                      ? "#000"
                                      : "rgb(47, 149, 220)"
                                  }
                                />
                              </ActionButton>
                            )}
                            {isDelete && (
                              <ActionButton
                                isPopup={isPopup}
                                onPress={() =>
                                  handleDeleteRow(
                                    row[rowKeyValue as string] as string,
                                  )
                                }
                                style={{
                                  cursor: "pointer",
                                  padding: pad,
                                  marginRight: 2,
                                  minWidth: minSize,
                                  minHeight: minSize,
                                  justifyContent: "center",
                                  alignItems: "center",
                                }}
                              >
                                <FeatherIcon
                                  name={"trash-2"}
                                  size={iconSize}
                                  color="rgb(178,34,34)"
                                />
                              </ActionButton>
                            )}
                          </>
                        );
                      };

                      const hasActions =
                        isDeliveryAttachment || isDetail || isEdit || isDelete;

                      return (isSmallDevice || isMobileDevice) && hasActions ? (
                        <Menu>
                          <MenuTrigger>
                            <View
                              style={{
                                padding: 12,
                                minHeight: 44,
                                justifyContent: "center",
                                alignItems: "center",
                              }}
                            >
                              <Icon
                                name="ellipsis-h"
                                size={18}
                                color="#9CA3AF"
                              />
                            </View>
                          </MenuTrigger>
                          <MenuOptions
                            customStyles={{
                              optionsContainer: {
                                width: "auto",
                                padding: 0,
                                borderRadius: 8,
                                marginLeft: -20,
                                marginTop: 5,
                              },
                            }}
                          >
                            <View
                              style={{
                                flexDirection: "row",
                                alignItems: "center",
                              }}
                            >
                              {renderActionButtons(true)}
                            </View>
                          </MenuOptions>
                        </Menu>
                      ) : (
                        <View
                          style={{
                            flexDirection: "row",
                            alignItems: "center",
                            backgroundColor: "transparent",
                          }}
                        >
                          {renderActionButtons(false)}
                        </View>
                      );
                    })()}
                  </View>
                ) : value === expandTriggerColumnKey ? (
                  <View
                    style={{
                      width: "100%",
                      flexDirection: "row",
                      alignItems: "center",
                      backgroundColor: "transparent",
                      justifyContent:
                        headers[value].textAlign === "right"
                          ? "flex-end"
                          : headers[value].textAlign === "center"
                            ? "center"
                            : "flex-start",
                    }}
                  >
                    <Text
                      style={{
                        fontSize: 12,
                        backgroundColor: "transparent",
                        textAlign: headers[value].textAlign,
                      }}
                      selectable={selectable ? true : false}
                    >
                      {columnsToNotHaveThousandSeparator?.includes(
                        headers[value].name,
                      )
                        ? `${row[value] ?? 0}`
                        : formatValueAsDataType(
                            row[value],
                            isDisplayTimeWithDate,
                          ) || 0}
                    </Text>
                    {showExpandTrigger?.(row) && (
                      <TouchableOpacity
                        onPress={() =>
                          handleExpandableRowIcon(
                            row,
                            displayExpandableIcons(
                              row,
                              expandRow,
                              expandAllRows,
                            ),
                          )
                        }
                        style={{ marginLeft: 6 }}
                      >
                        <Icon
                          name={switchToExpand ? "chevron-up" : "chevron-down"}
                          size={12}
                          color="rgb(47, 149, 220)"
                        />
                      </TouchableOpacity>
                    )}
                  </View>
                ) : (
                  <Text
                    style={{
                      fontSize: 12,
                      width: "100%",
                      textAlign: headers[value].textAlign,
                    }}
                    selectable={selectable ? true : false}
                  >
                    {columnsToNotHaveThousandSeparator?.includes(
                      headers[value].name,
                    )
                      ? `${
                          headers[value].name.includes("uom1") ||
                          headers[value].name.includes("Uom1")
                            ? `${row[value]} (${row["InvntryUom"]})`
                            : headers[value].name.includes("uom2") ||
                                headers[value].name.includes("Uom2")
                              ? `${row[value]} (${row["Uom2"]})`
                              : headers[value].name.includes("portal") ||
                                  headers[value].name.includes("Portal")
                                ? `${row[value] ? row[value] : ""}`
                                : `${row[value]}`
                        }`
                      : formatValueAsDataType(
                          row[value],
                          isDisplayTimeWithDate,
                        ) || 0}
                  </Text>
                )}
              </View>,
            );
          }

          if (["status", "ship_status"].includes(value)) {
            views.push(
              <View
                style={{
                  flex: headers[value].width,
                  borderColor: themeColors.borderSubtle,
                  alignItems: "flex-start",
                  justifyContent: "center",
                  backgroundColor: "transparent",
                  paddingHorizontal: 6,
                }}
                key={value}
              >
                <Badge
                  label={
                    formatStatusLabel
                      ? formatStatusLabel(row[value])
                      : row[value]
                  }
                  tintColor="white"
                  color={statusColorCodes[row[value]]}
                />
              </View>,
            );
          }

          if (itemTracker) {
            views.push(
              <View
                style={{
                  flex: headers[value].width,
                  borderColor: themeColors.borderSubtle,
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "transparent",
                  paddingHorizontal: 6,
                }}
                key={value}
              >
                {index === 0 && isRowsExpandable ? (
                  <View
                    style={{
                      flexDirection: "row",
                      alignSelf: "flex-start",
                      backgroundColor: "transparent",
                    }}
                  >
                    {!expandAllRows && (
                      <TouchableOpacity
                        onPress={() =>
                          handleExpandableRowIcon(
                            row,
                            displayExpandableIcons(
                              row,
                              expandRow,
                              expandAllRows,
                            ),
                          )
                        }
                      >
                        <Icon
                          name={displayExpandableIcons(
                            row,
                            expandRow,
                            expandAllRows,
                          )}
                          size={15}
                          color={
                            operationsScreen
                              ? themeColors.text
                              : "rgb(47, 149, 220)"
                          }
                        />
                      </TouchableOpacity>
                    )}
                    <Text
                      selectable={true}
                      style={[
                        operationsScreen && { fontWeight: "500" },
                        { userSelect: "text" } as any,
                      ]}
                    >
                      &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;{row[value]}
                    </Text>
                  </View>
                ) : ["progress"].includes(value) ? (
                  <CustomProgressBar progress={row[value]} />
                ) : ["delivery"].includes(value) ? (
                  <DeliveryProgressBar progress={row[value]} />
                ) : ["OrderStatus"].includes(value) ? (
                  <Text
                    style={{
                      fontSize: 12,
                      color: getStatusColor(row[value]), // Apply status color
                      fontWeight: "bold",
                      flexDirection: "row",
                      alignSelf: "flex-start",
                    }}
                  >
                    {row[value]}
                  </Text>
                ) : ["DaysLeft"].includes(value) ? (
                  <Text
                    style={{
                      fontSize: 12,
                      color: getDaysLeftColor(row[value]), // Apply status color
                      fontWeight: "bold",
                      flexDirection: "row",
                      alignSelf: "flex-start",
                    }}
                  >
                    {row[value] === 0 ? "" : row[value]}
                  </Text>
                ) : ["BPName"].includes(value) ? (
                  <Text
                    style={{
                      fontSize: 12,
                      width: "100%",
                      textAlign: ["name", "sales"].some((item) =>
                        value.toLowerCase().includes(item),
                      )
                        ? "left"
                        : ["TotalOrdered"].some((item) => value.includes(item))
                          ? "right"
                          : "left",
                      paddingRight: ["TotalOrdered"].some((item) =>
                        value.includes(item),
                      )
                        ? "50%"
                        : 0,
                      textTransform: "capitalize",
                    }}
                    selectable={selectable ? true : false}
                    onPress={() => handleDoublePress(value, row[value])}
                  >
                    {row[value] === 0 || row[value] == null
                      ? ""
                      : String(row[value])
                          .toLowerCase()
                          .replace(/^./, (char) => char.toUpperCase())}
                  </Text>
                ) : !["trackerActions_sm", "trackerActions"].includes(value) ? (
                  <Text
                    style={{
                      fontSize: 12,
                      width: "100%",
                      textAlign: ["name", "sales"].some((item) =>
                        value.toLowerCase().includes(item),
                      )
                        ? "left"
                        : ["TotalOrdered"].some((item) => value.includes(item))
                          ? "right"
                          : "left",
                      paddingRight: ["TotalOrdered"].some((item) =>
                        value.includes(item),
                      )
                        ? "50%"
                        : 0,
                    }}
                    selectable={selectable ? true : false}
                    onPress={() => handleDoublePress(value, row[value])}
                  >
                    {(() => {
                      const columnName = headers[value].name;
                      const cellValue = row[value];

                      if (
                        columnsToNotHaveThousandSeparator?.includes(columnName)
                      ) {
                        if (
                          columnName.includes("Uom1") ||
                          columnName.includes("uom1")
                        ) {
                          return `${cellValue} (${row["InvntryUom"]})`;
                        } else if (
                          columnName.includes("Uom2") ||
                          columnName.includes("uom2")
                        ) {
                          return `${cellValue} (${row["Uom2"]})`;
                        } else {
                          return `${cellValue}`;
                        }
                      } else if (
                        columnName?.includes("Order Qty") ||
                        columnName?.includes("orderQty")
                      ) {
                        return `${cellValue?.toLocaleString()}`;
                      } else {
                        return cellValue;
                      }
                    })()}
                  </Text>
                ) : ["trackerActions_sm"].includes(value) ? (
                  <View style={{ flexDirection: "row" }}>
                    <View>
                      <Menu>
                        <MenuTrigger>
                          <View
                            style={{
                              justifyContent: "center",
                              alignContent: "center",
                              paddingLeft: 10,
                              width: 25,
                            }}
                          >
                            <Text>
                              <SimpleLineIcons
                                name="options"
                                size={18}
                                color="grey"
                              />
                            </Text>
                          </View>
                        </MenuTrigger>
                        <MenuOptions
                          optionsContainerStyle={{
                            padding: 2,
                            zIndex: 999,
                            marginLeft: -25,
                            marginTop: -10,
                            width: 140,
                          }}
                        >
                          <View style={{ flexDirection: "row" }}>
                            <View style={styles.menuOptionContainer}>
                              <MenuOption
                                style={{
                                  flex: 1,
                                  alignItems: "center",
                                }}
                                onSelect={() => operationActions?.(row, "view")}
                              >
                                <Text>
                                  <MaterialCommunityIcons
                                    name="eye"
                                    size={20}
                                    color={themeColors.text}
                                    style={[styles.actionsIcons]}
                                  />
                                </Text>
                              </MenuOption>
                            </View>
                            <View style={styles.menuOptionContainer}>
                              <MenuOption
                                style={{
                                  flex: 1,
                                  alignItems: "center",
                                }}
                                onSelect={() => operationActions?.(row, "add")}
                              >
                                <Text>
                                  <MaterialCommunityIcons
                                    name="comment-outline"
                                    size={20}
                                    color={themeColors.text}
                                    style={[styles.actionsIcons]}
                                  />
                                </Text>
                              </MenuOption>
                            </View>
                            <View style={styles.menuOptionContainer}>
                              <MenuOption
                                style={{
                                  flex: 1,
                                  alignItems: "center",
                                }}
                                onSelect={() =>
                                  operationActions?.(row, "history")
                                }
                              >
                                <Text>
                                  <MaterialCommunityIcons
                                    name="clock-outline"
                                    size={20}
                                    color={themeColors.text}
                                    style={[styles.actionsIcons]}
                                  />
                                </Text>
                              </MenuOption>
                            </View>
                          </View>
                        </MenuOptions>
                      </Menu>
                    </View>
                  </View>
                ) : (
                  <View
                    style={{
                      borderColor: themeColors.borderSubtle,
                      alignItems: "center",
                      alignSelf: "flex-start",
                    }}
                  >
                    <View style={{ flexDirection: "row" }}>
                      <TouchableOpacity
                        onPress={() => operationActions?.(row, "view")}
                      >
                        <MaterialCommunityIcons
                          name="eye"
                          size={20}
                          color={themeColors.text}
                          style={[styles.actionsIcons]}
                        />
                      </TouchableOpacity>
                      <TouchableOpacity
                        onPress={() => operationActions?.(row, "add")}
                      >
                        <MaterialCommunityIcons
                          name="comment-outline"
                          size={20}
                          color={themeColors.text}
                          style={[styles.actionsIcons]}
                        />
                      </TouchableOpacity>

                      <TouchableOpacity
                        onPress={() => operationActions?.(row, "history")}
                      >
                        <MaterialCommunityIcons
                          name="clock-outline"
                          size={20}
                          color={themeColors.text}
                          style={[styles.actionsIcons]}
                        />
                      </TouchableOpacity>
                    </View>
                  </View>
                )}
              </View>,
            );
          }
        }
      });

      if (row) {
        rowsViews.push(
          <React.Fragment key={index}>
            <DataTable.Row
              style={{
                backgroundColor: operationsScreen
                  ? themeColors.surface
                  : switchToExpand
                    ? themeColors.surfaceSubtle
                    : themeColors.surface,
                borderBottomColor: themeColors.borderSubtle,
                borderBottomWidth: 1,
              }}
              key={row[rowKeyValue as string] || index}
              onPress={
                !nestedHeader && !selectable
                  ? () => operationActions?.(row, "view")
                  : undefined
              }
            >
              {views}
            </DataTable.Row>

            {isRowsExpandable && switchToExpand && !itemTracker && (
              <NestedDataTable
                itemsPerPage={itemsPerPage}
                isNestedDataLoaded={isNestedDataLoaded}
                nestedHeader={nestedHeader}
                nestedRows={row[nestedItemKeyValue as string]}
                rowKeyValue={rowKeyValue}
                isDetail={isSubDetail}
                setModalVisible={setModalVisible}
                handleRowClick={handleRowClick}
                setSelected={setSelected}
                key="rowKeyValue"
                nestedColumnsToNotHaveThousandSeparator={
                  columnsToNotHaveThousandSeparator
                }
                isDisplayTimeWithDate={isDisplayTimeWithDate}
                handleNestedSortColumn={handleNestedSortColumn}
                disableHeader={disableHeader}
                operationActions={
                  disableNestedRowActions ? undefined : operationActions
                }
                hideInfoIcon={hideInfoIcon}
              />
            )}
          </React.Fragment>,
        );
      }
    });
    return rowsViews;
  };

  return (
    <View style={{ flex: 1 }}>
      <View
        style={{
          position: "sticky",
          top: 0,
          zIndex: 1000,
          backgroundColor: themeColors.surface,
          ...(topCornerRadius
            ? {
                borderTopLeftRadius: topCornerRadius,
                borderTopRightRadius: topCornerRadius,
                overflow: "hidden" as any,
              }
            : null),
        }}
      >
        <ThemeProvider
          theme={{
            ...paperTheme,
            dark: isDark,
            colors: {
              ...paperTheme.colors,
              text: themeColors.text,
              onSurface: themeColors.text, // This controls the sort icon color
            },
          }}
        >
          <DataTable.Header style={{ height: "auto", minHeight: 48 }}>
            {headersView(headers)}
          </DataTable.Header>
        </ThemeProvider>
      </View>

      <ScrollView style={{ flex: 1 }}>
        <OrderStatusHelp
          visible={statusHelpVisible}
          onClose={() => setStatusHelpVisible(false)}
        />
        {isDataLoading ? (
          <View>
            <Spinner
              size="small"
              color="#74BBFF"
              styles={{
                flexDirection: "row",
                marginTop: "5%",
                marginBottom: "5%",
              }}
            />
          </View>
        ) : rows.length > 0 ? (
          cellsView(
            rows.slice(
              page * _itemsPerPage,
              page * _itemsPerPage + _itemsPerPage,
            ),
            headers,
          )
        ) : (
          <NoDataBanner />
        )}
        {!hidePagination && (
          <View
            style={
              bottomCornerRadius
                ? {
                    backgroundColor: themeColors.surface,
                    borderBottomLeftRadius: bottomCornerRadius,
                    borderBottomRightRadius: bottomCornerRadius,
                    overflow: "hidden",
                  }
                : undefined
            }
          >
            <ThemeProvider
              theme={{
                ...paperTheme,
                dark: isDark,
                colors: {
                  ...paperTheme.colors,
                  text: themeColors.text,
                  onSurface: themeColors.textSecondary,
                },
              }}
            >
              <DataTable.Pagination
                page={page}
                numberOfPages={Math.ceil(rows.length / _itemsPerPage)}
                onPageChange={(page) => setPage(page)}
                label={`${fromPage(page, itemsPerPage, rows)} of ${rows.length}`}
                showFastPaginationControls
                numberOfItemsPerPage={_itemsPerPage}
              />
            </ThemeProvider>
          </View>
        )}
      </ScrollView>
    </View>
  );
};

function createStyles(colors: any) {
  return StyleSheet.create({
    collapsableView: {
      flex: 1,
      borderStyle: "solid",
      borderColor: "rgba(47, 149, 220, 0.5)",
      borderWidth: 1,
    },
    actionsIcons: {
      paddingHorizontal: 8,
      cursor: "pointer",
    },
    menuOptionContainer: {
      flex: 1,
      marginHorizontal: 2,
    },
  });
}
